/**
 * Difficulty + the skill stages the game walks through.
 *
 * The player picks a difficulty in the start menu; within it the game
 * auto-advances through the same skill ladder (count one flavor → count a mix →
 * pay with coins → make change). Difficulty scales the NUMBERS, not the skills:
 * Easy uses small counts / few flavors / no change, Hard uses bigger counts,
 * all eight flavors, larger prices and change-making.
 *
 * TWEAK ME: retune the whole curve from DIFFICULTIES below. All numbers stay
 * within 1–10 (pre-reader friendly).
 */
import { FLAVORS } from '../assets/palette.ts';

export type BaseId = 'cone' | 'cup';

/** One flavor and how many scoops of it the order wants. */
export interface ScoopReq {
  flavor: string;
  count: number;
}

export interface Order {
  base: BaseId;
  scoops: ScoopReq[];
  /** Total scoops across all flavors (what the kid counts to). */
  total: number;
  /** Payment due, in coin units (pay/change stages only). */
  price?: number;
  /** A single coin the customer hands over, larger than price (change stage). */
  paidWith?: number;
}

export type DifficultyId = 'easy' | 'medium' | 'hard';

export interface Difficulty {
  id: DifficultyId;
  /** Max scoops in a single-flavor counting order. */
  singleMax: number;
  /** Max scoops per flavor in a two-flavor mixed order. */
  mixedMax: number;
  /** How many flavor buttons appear on the shelf (a rotating subset of all 8). */
  flavorChoices: number;
  /** Max price (coins) on the pay stage. */
  priceMax: number;
  /** Whether change-making rounds are included. */
  doChange: boolean;
  /** Successful rounds within a skill stage before advancing to the next. */
  roundsPerStage: number;
}

export const DIFFICULTIES: Record<DifficultyId, Difficulty> = {
  easy: { id: 'easy', singleMax: 3, mixedMax: 2, flavorChoices: 3, priceMax: 3, doChange: false, roundsPerStage: 3 },
  medium: { id: 'medium', singleMax: 5, mixedMax: 3, flavorChoices: 5, priceMax: 6, doChange: true, roundsPerStage: 3 },
  hard: { id: 'hard', singleMax: 5, mixedMax: 4, flavorChoices: 8, priceMax: 9, doChange: true, roundsPerStage: 4 },
};

const ALL_FLAVOR_IDS = FLAVORS.map((f) => f.id);

const randInt = (min: number, max: number): number =>
  Math.floor(Math.random() * (max - min + 1)) + min;
const pick = <T,>(arr: T[]): T => arr[randInt(0, arr.length - 1)];

function pickDistinct<T>(arr: T[], n: number): T[] {
  const pool = [...arr];
  const out: T[] = [];
  for (let i = 0; i < n && pool.length; i++) {
    out.push(pool.splice(randInt(0, pool.length - 1), 1)[0]);
  }
  return out;
}

/** Greedy 5/2/1 breakdown — the canonical coin row shown for a price. */
export function coinsForPrice(price: number): number[] {
  const out: number[] = [];
  let rest = price;
  for (const v of [5, 2, 1]) {
    while (rest >= v) {
      out.push(v);
      rest -= v;
    }
  }
  return out;
}

/** One rung of the skill ladder. */
export interface Stage {
  id: string;
  doPay: boolean;
  doChange: boolean;
  makeOrder: (d: Difficulty) => Order;
}

const countSingle: Stage = {
  id: 'count',
  doPay: false,
  doChange: false,
  makeOrder(d) {
    const flavor = pick(ALL_FLAVOR_IDS);
    const count = randInt(1, d.singleMax);
    return { base: 'cone', scoops: [{ flavor, count }], total: count };
  },
};

const countMixed: Stage = {
  id: 'mixed',
  doPay: false,
  doChange: false,
  makeOrder(d) {
    const [a, b] = pickDistinct(ALL_FLAVOR_IDS, 2);
    const ca = randInt(1, d.mixedMax);
    const cb = randInt(1, d.mixedMax);
    return {
      base: pick(['cone', 'cup'] as BaseId[]),
      scoops: [
        { flavor: a, count: ca },
        { flavor: b, count: cb },
      ],
      total: ca + cb,
    };
  },
};

const payStage: Stage = {
  id: 'pay',
  doPay: true,
  doChange: false,
  makeOrder(d) {
    const flavor = pick(ALL_FLAVOR_IDS);
    const count = randInt(1, 3);
    const price = randInt(2, d.priceMax);
    return { base: 'cone', scoops: [{ flavor, count }], total: count, price };
  },
};

const changeStage: Stage = {
  id: 'change',
  doPay: true,
  doChange: true,
  makeOrder(d) {
    const flavor = pick(ALL_FLAVOR_IDS);
    const count = randInt(1, 3);
    // Keep the price low enough that a single bigger coin overpays it.
    const price = randInt(1, Math.min(4, d.priceMax - 1));
    const paidWith = [2, 5].find((v) => v > price) ?? 5;
    return { base: 'cone', scoops: [{ flavor, count }], total: count, price, paidWith };
  },
};

/** The ordered skill stages for a difficulty (change only when enabled). */
export function stagesFor(d: Difficulty): Stage[] {
  const base = [countSingle, countMixed, payStage];
  return d.doChange ? [...base, changeStage] : base;
}

export { ALL_FLAVOR_IDS };
